import { View, StyleSheet, Pressable, Text } from "react-native";
import { useTranslation } from "react-i18next";
import { ms } from "@/ui/moderateScale";
import { formatDateTime, getBasePath } from "@/models/utils";
import PageContainer from "@/ui/PageContainer";
import { Colors } from "@/constants";
import Menu from "@/ui/Menu";
import { useUserSlice } from "@/models/useUserSlice";
import Icon from "@/ui/Icon";
import * as Types from "@/models/types";
import List from "@/ui/List";
import { useRouter } from "@/_router";

function History() {
  const { t } = useTranslation();
  const chats = useUserSlice((state) => state.chats);
  const chatsNextCursor = useUserSlice((state) => state.chatsNextCursor);
  const router = useRouter();

  const _onEndReached = async () => {
    if (!chatsNextCursor) return;

    useUserSlice.getState().chat().list({ nextCursor: chatsNextCursor });
  };

  const _onChatPress = (chat: Types.Chat) => {
    router.replace(`${getBasePath()}/?roomId=${chat.id}`);
  };

  if (!chats?.length) {
    return (
      <PageContainer
        bgColor={Colors.bgGray}
        HeaderComponent={
          <PageContainer.MobileHeader
            screenHeader={{ title: t("history.title") }}
          />
        }
      >
        <Text style={styles.noChatsText}>{t("history.noChats")}</Text>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      bgColor={Colors.bgGray}
      HeaderComponent={
        <PageContainer.MobileHeader
          screenHeader={{ title: t("history.title") }}
        />
      }
    >
      <View
        style={{
          flex: 1,
          paddingHorizontal: ms(16),
          paddingTop: ms(16),
          paddingBottom: ms(100),
        }}
      >
        <List
          data={chats}
          keyExtractor={(item) => item.id}
          onEndReached={_onEndReached}
          renderItem={({ item }) => (
            <History.ListItem
              key={item.id}
              item={item}
              onPress={() => _onChatPress(item)}
            />
          )}
        />
      </View>
    </PageContainer>
  );
}

History.ListItem = (params: { item: Types.Chat; onPress: () => void }) => {
  const { t } = useTranslation();

  return (
    <Pressable onPress={params.onPress} style={styles.container}>
      <View style={styles.contentContainer}>
        <Text numberOfLines={2} style={styles.title}>
          {params.item.title}
        </Text>
        <Text style={styles.date}>{formatDateTime(params.item.createdAt)}</Text>
      </View>

      <Menu
        actions={[
          {
            text: t("history.renameChat"),
            onPress: () =>
              useUserSlice.getState().chat().rename(params.item.id),
          },
          {
            text: t("history.deleteChat"),
            destructive: true,
            onPress: () =>
              useUserSlice.getState().chat().delete({ chatId: params.item.id }),
          },
        ]}
        actionComponent={
          <View
            style={{
              width: ms(48),
              height: ms(48),
              alignItems: "center",
              justifyContent: "center",
              marginRight: -ms(12),
            }}
          >
            <Icon icon="moreHorizontal" style={styles.icon} />
          </View>
        }
      />
    </Pressable>
  );
};

const styles = StyleSheet.create({
  spacer: {
    height: ms(12),
  },
  container: {
    height: ms(90),
    justifyContent: "center",
    backgroundColor: Colors.white,
    paddingHorizontal: ms(20),
    borderRadius: ms(16),
    flexDirection: "row",
    alignItems: "center",
    gap: ms(16),
    marginBottom: ms(8),
  },
  pendingContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: ms(16),
  },
  contentContainer: {
    flex: 1,
  },
  title: {
    fontSize: ms(16),
    fontWeight: "400",
    marginBottom: ms(6),
  },
  date: {
    fontWeight: "300",
    fontSize: ms(12),
    color: Colors.subtitle,
  },
  icon: {
    width: ms(20),
    height: ms(20),
    tintColor: Colors.subtitle,
  },
  pendingStatus: {
    fontWeight: "300",
    fontSize: ms(12),
    color: Colors.warning,
  },
  errorStatus: {
    fontWeight: "300",
    fontSize: ms(12),
    color: Colors.error,
  },
  emojiContainer: {
    backgroundColor: Colors.text + "10",
    width: ms(40),
    height: ms(40),
    borderRadius: ms(32),
    alignItems: "center",
    justifyContent: "center",
  },
  noChatsText: {
    marginTop: ms(32),
    fontSize: ms(16),
    fontWeight: "400",
    color: Colors.text + "90",
    textAlign: "center",
  },
});

export default History;
